<template>
  <div class="ds-page">
    <div class="page-header d-print-none">
      <div class="row align-items-center">
        <div class="col">
          <div class="page-pretitle">Companies / WPS</div>
          <h2 class="page-title">WPS Request Items</h2>
          <p class="page-subtitle">Review request item details, bank information, and disbursement progress.</p>
        </div>
        <div class="col-auto ms-auto">
          <button @click="downloadHandler" class="ds-btn ds-btn--blue mr-2" :disabled="!list.length">
            <font-awesome-icon icon="download" />
            Download
          </button>
          <button @click="goBack" class="ds-btn ds-btn--secondary">
            <font-awesome-icon icon="arrow-left" />
            Back to List
          </button>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body companies-wps-detail__meta">
        <div class="companies-wps-detail__meta-item">
          <span class="companies-wps-detail__meta-label">Request Number</span>
          <span class="companies-wps-detail__meta-value">{{ requestNumber }}</span>
        </div>
        <div class="companies-wps-detail__meta-item">
          <span class="companies-wps-detail__meta-label">Showing</span>
          <span class="companies-wps-detail__meta-value">{{ total_rows }} item{{ total_rows === 1 ? "" : "s" }}</span>
        </div>
        <div class="companies-wps-detail__meta-item">
          <span class="companies-wps-detail__meta-label">Search Scope</span>
          <span class="companies-wps-detail__meta-value">Request item details</span>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-card__body">
        <div class="wps-filter-header">
          <span class="ds-filters__label">Filters</span>
          <span class="ds-filters__hint">Use status and bank field filters to narrow request items.</span>
        </div>

        <div class="wps-filter-two-col">
          <div class="wps-filter-left">
            <div class="wps-filter-grid-left">
              <div class="ds-field">
                <label class="ds-field__label">Filter By</label>
                <el-select
                  v-model="filters.filter_by"
                  size="small"
                  class="w-100"
                  placeholder="Select filter"
                  @change="handleFilterByChange"
                >
                  <el-option v-for="item in filterByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Filter Value</label>
                <el-select v-model="filters.filter_value" size="small" class="w-100" placeholder="Select value">
                  <el-option
                    v-for="item in selectedFilterValueOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Sort By</label>
                <el-select v-model="filters.sort_by" size="small" class="w-100" placeholder="Sort by">
                  <el-option v-for="item in sortByOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>

              <div class="ds-field">
                <label class="ds-field__label">Order</label>
                <el-select v-model="filters.sort_type" size="small" class="w-100" placeholder="Order">
                  <el-option v-for="item in sortTypeOptions" :key="item.value" :label="item.label" :value="item.value" />
                </el-select>
              </div>
            </div>
          </div>

          <div class="wps-filter-sep"></div>

          <div class="wps-filter-right">
            <div class="ds-field">
              <label class="ds-field__label">Search By</label>
              <el-select
                v-model="filters.search_by"
                size="small"
                class="w-100"
                placeholder="Select search"
                @change="handleSearchByChange"
              >
                <el-option v-for="item in searchByOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </div>

            <div class="ds-field">
              <label class="ds-field__label">Search Value</label>
              <el-input
                v-model="filters.search_value"
                clearable
                size="small"
                :placeholder="searchPlaceholder"
                @clear="searchHandler"
              />
            </div>

            <div class="wps-filter-actions">
              <button @click="resetHandler" class="ds-btn ds-btn--secondary ds-btn--sm">Reset</button>
              <button @click="searchHandler" class="ds-btn ds-btn--blue ds-btn--sm">
                <font-awesome-icon icon="search" />
                Search
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ds-card">
      <div class="ds-table-wrap">
        <table class="ds-table" v-loading="is_loading">
          <thead>
            <tr>
              <th>Request Date</th>
              <th class="text-right">Base Salary</th>
              <th class="text-right">Allowance</th>
              <th class="text-right">Bonus</th>
              <th class="text-right">Deduction</th>
              <th class="text-right">Gross Amount</th>
              <th class="text-right">Net Amount</th>
              <th>Bank Name</th>
              <th>Account Number</th>
              <th>Account Name</th>
              <th>Disburse Status</th>
              <th>Reference</th>
              <th>Notes</th>
              <th>Disbursed At</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in list" :key="i">
              <td>
                <span class="ds-text-sm ds-text-muted">{{ item.request_date_str }}</span>
              </td>
              <td class="text-right">{{ item.base_salary_str }}</td>
              <td class="text-right">{{ item.allowance_str }}</td>
              <td class="text-right">{{ item.bonus_str }}</td>
              <td class="text-right">{{ item.deduction_str }}</td>
              <td class="text-right">
                <span class="ds-amount">{{ item.gross_amount_str }}</span>
              </td>
              <td class="text-right">
                <span class="ds-amount">{{ item.net_amount_str }}</span>
              </td>
              <td>{{ item.bank_name || "—" }}</td>
              <td>{{ item.bank_account_number || "—" }}</td>
              <td>{{ item.bank_account_name || "—" }}</td>
              <td>
                <span class="ds-pill" :class="statusPillClass(item.normalized_disburse_status)">
                  {{ statusLabel(item.normalized_disburse_status) }}
                </span>
              </td>
              <td>{{ item.disbursement_reference || "—" }}</td>
              <td class="companies-wps-detail__notes">{{ item.disbursement_notes || "—" }}</td>
              <td>{{ item.disbursed_at_str }}</td>
            </tr>
          </tbody>
        </table>

        <el-empty v-if="!total_rows" description="No WPS request items found" :image="emptyStateImage" class="ds-empty" />
      </div>

      <pagination :page="page" :per_page="per_page" :list="list" :total_rows="total_rows" @pageChange="pageChangeHandler" />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { GET_DETAIL_WPS } from "@/store/modules/wps";
import emptyStateImage from "@/assets/images/empty-state.png";
import Pagination from "@/ui/components/Pagination.vue";

const DEFAULT_FILTER_VALUE = "";

export default {
  name: "CompaniesWpsDetail",
  components: {
    Pagination,
  },
  metaInfo: {
    title: "WPS Request Items",
  },
  computed: {
    requestNumber() {
      return this.$route.query.request_number || `#${this.$route.params.id}`;
    },
    selectedFilterValueOptions() {
      return this.filters.filter_by === "disbursement_status" ? this.disbursementStatusOptions : this.statusOptions;
    },
    searchPlaceholder() {
      const placeholderMap = {
        bank_account_name: "Input bank account name",
        bank_account_number: "Input bank account number",
        bank_name: "Input bank name",
      };
      return placeholderMap[this.filters.search_by] || "Input search value";
    },
  },
  data() {
    return {
      emptyStateImage,
      is_loading: false,
      page: 1,
      per_page: 10,
      total_rows: 0,
      list: [],
      filters: {
        filter_by: "status",
        filter_value: DEFAULT_FILTER_VALUE,
        search_by: "bank_account_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      },
      filterByOptions: [
        { value: "status", label: "Status" },
        { value: "disbursement_status", label: "Disburse Status" },
      ],
      statusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL", label: "Waiting Approval" },
        { value: "APPROVED", label: "Approved" },
        { value: "REJECTED", label: "Rejected" },
      ],
      disbursementStatusOptions: [
        { value: "", label: "All" },
        { value: "WAITING_APPROVAL", label: "Waiting Approval" },
        { value: "WAITING_DISBURSEMENT", label: "Waiting Disbursement" },
        { value: "PROCESS_DISBURSEMENT", label: "Processing Disbursement" },
        { value: "COMPLETED", label: "Completed" },
        { value: "FAILED", label: "Failed" },
      ],
      searchByOptions: [
        { value: "bank_account_name", label: "Bank Account Name" },
        { value: "bank_account_number", label: "Bank Account Number" },
        { value: "bank_name", label: "Bank Name" },
      ],
      sortByOptions: [
        { value: "created_at", label: "Request Date" },
        { value: "bank_account_name", label: "Bank Account Name" },
        { value: "bank_account_number", label: "Bank Account Number" },
        { value: "bank_name", label: "Bank Name" },
        { value: "disbursed_at", label: "Disbursed At" },
      ],
      sortTypeOptions: [
        { value: "desc", label: "Descending" },
        { value: "asc", label: "Ascending" },
      ],
    };
  },
  async mounted() {
    await this.getList();
  },
  methods: {
    goBack() {
      this.$router.push({ name: "Wage Payment Service" });
    },
    formatCurrency(value) {
      return `Rp. ${Number(value || 0).toLocaleString("id-ID")}`;
    },
    normalizeDisbursementStatus(status) {
      return status || "—";
    },
    statusPillClass(status) {
      const value = String(status || "").toUpperCase();
      switch (value) {
        case "APPROVED":
        case "COMPLETED":
          return "ds-pill--success";
        case "REJECTED":
        case "FAILED":
          return "ds-pill--danger";
        case "PROCESS_DISBURSEMENT":
        case "PROCESSING":
          return "ds-pill--info";
        case "WAITING_APPROVAL":
        case "WAITING_DISBURSEMENT":
        case "WAITING_PAYMENT":
          return "ds-pill--warning";
        default:
          return "ds-pill--gray";
      }
    },
    statusLabel(status) {
      if (!status || status === "—") return "—";
      return String(status)
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    mapItem(item) {
      const normalizedDisburseStatus = item.disbursement_status || item.disburse_status || "";

      return {
        ...item,
        request_date_str: item.created_at ? moment(item.created_at).format("DD MMM YYYY, HH:mm") : "—",
        base_salary_str: this.formatCurrency(item.base_salary),
        allowance_str: this.formatCurrency(item.allowance),
        bonus_str: this.formatCurrency(item.bonus),
        deduction_str: this.formatCurrency(item.deduction),
        gross_amount_str: this.formatCurrency(item.gross_amount),
        net_amount_str: this.formatCurrency(item.net_amount),
        disbursed_at_str: item.disbursed_at ? moment(item.disbursed_at).format("DD MMM YYYY, HH:mm") : "—",
        normalized_disburse_status: this.normalizeDisbursementStatus(normalizedDisburseStatus),
      };
    },
    buildQuery(extraParams = {}) {
      const query = {
        page: this.page,
        size: this.per_page,
        per_page: this.per_page,
        sort: this.filters.sort_type === "desc" ? `-${this.filters.sort_by}` : this.filters.sort_by,
        filter_date: "created_at",
        ...extraParams,
      };

      if (this.filters.filter_by && this.filters.filter_value) {
        query[this.filters.filter_by] = this.filters.filter_value;
      }

      if (this.filters.search_by && this.filters.search_value) {
        query.search = this.filters.search_by;
        query.search_value = this.filters.search_value;
      }

      return query;
    },
    handleFilterByChange() {
      this.filters.filter_value = "";
    },
    handleSearchByChange() {
      this.filters.search_value = "";
    },
    searchHandler() {
      this.page = 1;
      this.getList();
    },
    resetHandler() {
      this.filters = {
        filter_by: "status",
        filter_value: DEFAULT_FILTER_VALUE,
        search_by: "bank_account_name",
        search_value: "",
        sort_by: "created_at",
        sort_type: "desc",
      };
      this.page = 1;
      this.getList();
    },
    async downloadHandler() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const response = await this.$store.dispatch(GET_DETAIL_WPS, {
          id: this.$route.params.id,
          query: this.buildQuery({
            action: "download",
          }),
        });
        const data = response?.data ?? response;
        const fileName = `${String(this.requestNumber || "wps-request").replace(/[^\w.-]+/g, "_")}-items.csv`;

        this.$message({
          title: "Success",
          type: "success",
          message: "Success! Your CSV download will start shortly.",
        });

        this.$nextTick(() => {
          const anchor = document.createElement("a");
          anchor.href = `data:text/csv;charset=utf-8,${encodeURIComponent(data)}`;
          anchor.target = "_blank";
          anchor.download = fileName;
          anchor.click();
        });
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
    async pageChangeHandler(page) {
      this.page = page;
      await this.getList();
    },
    async getList() {
      this.is_loading = true;
      this.$parent.showLoader();
      try {
        const data = await this.$store.dispatch(GET_DETAIL_WPS, {
          id: this.$route.params.id,
          query: this.buildQuery(),
        });
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        this.list = rows.map((item) => this.mapItem(item));
        this.total_rows = data?.count || 0;
      } finally {
        this.$parent.hideLoader();
        this.is_loading = false;
      }
    },
  },
};
</script>

<style scoped>
.companies-wps-detail__meta {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  align-items: center;
}

.companies-wps-detail__meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.companies-wps-detail__meta-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ds-text-hint);
}

.companies-wps-detail__meta-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--ds-text-base);
}

.companies-wps-detail__notes {
  max-width: 220px;
  white-space: normal;
  line-height: 1.4;
}

.wps-filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.wps-filter-two-col {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0 20px;
  align-items: start;
}

.wps-filter-grid-left {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 12px;
  align-items: end;
}

.wps-filter-sep {
  width: 1px;
  background: rgba(101, 109, 119, 0.12);
  align-self: stretch;
  margin-top: 2px;
}

.wps-filter-right {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.wps-filter-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}

@media (max-width: 991px) {
  .companies-wps-detail__meta {
    grid-template-columns: 1fr;
  }

  .wps-filter-two-col {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .wps-filter-sep {
    display: none;
  }

  .wps-filter-grid-left {
    grid-template-columns: 1fr;
  }
}
</style>
